import React, { useState, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigationType } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import LoadingScreen from './components/common/LoadingScreen/LoadingScreen';
import NetworkBanner from './components/common/NetworkBanner/NetworkBanner';
import OneSignal from 'react-onesignal';
import OneSignalInit from './components/features/OneSignalInit/OneSignalInit';
import PendingApproval from './pages/PendingApproval/PendingApproval';
import { doc, onSnapshot } from "firebase/firestore";
import { db } from './config/firebase';
import { isAdmin, isSviluppatore } from './utils/roles';

const Login = lazy(() => import('./pages/Login/Login'));
const Register = lazy(() => import('./pages/Register/Register'));
const Menu = lazy(() => import('./components/layout/Menu/Menu'));
const Dashboard = lazy(() => import('./pages/Dashboard/Dashboard'));
const Prenotazioni = lazy(() => import('./pages/Prenotazioni/Prenotazioni'));
const Profilo = lazy(() => import('./pages/Profilo/Profilo'));
const AdminPanel = lazy(() => import('./pages/AdminPanel/AdminPanel'));
const ScheduleManager = lazy(() => import('./pages/ScheduleManager/ScheduleManager'));
const SessionDetail = lazy(() => import('./pages/SessionDetail/SessionDetail'));
const DevPanel = lazy(() => import('./pages/DevPanel/DevPanel'));
const DeleteAccount = lazy(() => import('./pages/DeleteAccount/DeleteAccount'));


const ProtectedRoute = ({ children, isLoggedIn, user, handleLogout, requiredRole }) => {
    if (!isLoggedIn) {
        return <Navigate to="/" replace />;
    }

    // Se l'utente è loggato ma è ancora "Disattivo", mostriamo la schermata di attesa
    if (user?.abbonamento?.stato === "Disattivo") {
        return <PendingApproval onLogout={handleLogout} />;
    }

    // Controllo ruolo
    if (requiredRole === 'admin' && !isAdmin(user)) return <Navigate to="/menu" replace />;
    if (requiredRole === 'sviluppatore' && !isSviluppatore(user)) return <Navigate to="/menu" replace />;

    return children;
};

const AnimatedRoutes = ({ isLoggedIn, user, handleLogin, handleLogout }) => {
    const location = useLocation();
    const navType = useNavigationType();

    // Fix iOS Swipe-Back: previene il reset nativo dello scroll che causa flickering
    React.useEffect(() => {
        if ('scrollRestoration' in window.history) {
            window.history.scrollRestoration = 'manual';
        }
    }, []);

    // Ripristino scroll solo se navighiamo in avanti. Su POP (back swipe), 
    // evitiamo di portare a 0,0 prima che l'animazione nativa sia finita.
    React.useEffect(() => {
        if (navType !== 'POP') {
            // Leggero delay per aspettare che la nuova vista sia pronta al rendering
            setTimeout(() => window.scrollTo(0, 0), 50);
        }
    }, [location.pathname, navType]);

    // Se è un POP (swipe back locale di iOS), rimuoviamo AnimatePresence
    // iOS fa la transizione a livello OS con i suoi snapshot. Se framer-motion
    // cerca di animare in "exit", si crea lo sfarfallio e il doppio movimento.
    const isPop = navType === 'POP';

    const childRoutes = (
        <Routes location={location} key={location.pathname}>
            {/* Public Routes */}
            <Route path="/" element={
                isLoggedIn ? <Navigate to="/menu" replace /> : <Login onLogin={handleLogin} />
            } />
            <Route path="/register" element={<Register />} />

            {/* Protected Routes */}
            <Route path="/menu" element={
                <ProtectedRoute isLoggedIn={isLoggedIn} user={user} handleLogout={handleLogout}>
                    <Menu onLogout={handleLogout} />
                </ProtectedRoute>
            } />

            <Route path="/corsi" element={
                <ProtectedRoute isLoggedIn={isLoggedIn} user={user}>
                    <Dashboard />
                </ProtectedRoute>
            } />

            <Route path="/prenotazioni" element={
                <ProtectedRoute isLoggedIn={isLoggedIn} user={user}>
                    <Prenotazioni />
                </ProtectedRoute>
            } />

            <Route path="/profilo" element={
                <ProtectedRoute isLoggedIn={isLoggedIn} user={user} handleLogout={handleLogout}>
                    <Profilo onLogout={handleLogout} />
                </ProtectedRoute>
            } />

            <Route path="/admin" element={
                <ProtectedRoute isLoggedIn={isLoggedIn} user={user} handleLogout={handleLogout} requiredRole="admin">
                    <AdminPanel />
                </ProtectedRoute>
            } />

            <Route path="/admin/palinsesto" element={
                <ProtectedRoute isLoggedIn={isLoggedIn} user={user} handleLogout={handleLogout} requiredRole="admin">
                    <ScheduleManager />
                </ProtectedRoute>
            } />

            <Route path="/sessione/:id" element={
                <ProtectedRoute isLoggedIn={isLoggedIn} user={user}>
                    <SessionDetail />
                </ProtectedRoute>
            } />

            <Route path="/dev" element={
                <ProtectedRoute isLoggedIn={isLoggedIn} user={user} handleLogout={handleLogout} requiredRole="sviluppatore">
                    <DevPanel />
                </ProtectedRoute>
            } />

            <Route path="/elimina-account" element={
                <ProtectedRoute isLoggedIn={isLoggedIn} user={user} handleLogout={handleLogout}>
                    <DeleteAccount />
                </ProtectedRoute>
            } />

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );

    if (isPop) {
        return childRoutes;
    }

    return (
        <AnimatePresence mode="wait">
            {childRoutes}
        </AnimatePresence>
    );
};

function App() {
    const [user, setUser] = useState(() => {
        const storedUser = localStorage.getItem('userLogged');
        try {
            return storedUser ? JSON.parse(storedUser) : null;
        } catch {
            return null;
        }
    });
    const [isLoggedIn, setIsLoggedIn] = useState(!!user);

    // Keep the logged in user synchronized live from Firestore
    const userId = user?.uid;
    React.useEffect(() => {
        if (!userId) return;

        console.time('[App] listener Firestore utente');
        const unsubscribe = onSnapshot(doc(db, "users", userId), (docSnap) => {
            console.timeEnd('[App] listener Firestore utente');
            if (docSnap.exists()) {
                const data = docSnap.data();

                setUser(prevUser => {
                    if (!prevUser) return prevUser;

                    const updatedUser = {
                        ...prevUser,
                        abbonamento: data.abbonamento,
                        ruolo: data.ruolo
                    };

                    // Controllo più snello invece di JSON.stringify sull'intero oggetto
                    const hasChanged =
                        prevUser.ruolo !== updatedUser.ruolo ||
                        prevUser.abbonamento?.stato !== updatedUser.abbonamento?.stato ||
                        prevUser.abbonamento?.scadenza !== updatedUser.abbonamento?.scadenza ||
                        prevUser.abbonamento?.limite_settimanale !== updatedUser.abbonamento?.limite_settimanale;

                    if (hasChanged) {
                        // Salva solo i campi di display, non dati sensibili/autorizzativi
                        const displayData = {
                            uid: updatedUser.uid,
                            nome: updatedUser.nome,
                            sigla: updatedUser.sigla,
                            iniziali: updatedUser.iniziali,
                        };
                        localStorage.setItem('userLogged', JSON.stringify(displayData));
                        return updatedUser;
                    }

                    return prevUser;
                });
            }
        }, (error) => {
            console.warn("Sistema: Listener Firestore interrotto (fisiologico durante il logout).", error.message);
        });

        return () => unsubscribe();
    }, [userId]);

    const handleLogin = () => {
        console.time('[App] handleLogin → render autenticato');
        const storedUser = localStorage.getItem('userLogged');
        if (storedUser) {
            try {
                setUser(JSON.parse(storedUser));
            } catch (e) {
                console.error("Invalid user JSON");
            }
        }
        setIsLoggedIn(true);
        console.timeEnd('[App] handleLogin → render autenticato');
    };

    const handleLogout = async () => {
        try {
            await OneSignal.logout();
        } catch (e) {
            console.error("OneSignal logout error", e);
        }
        localStorage.removeItem('userLogged');
        setUser(null);
        setIsLoggedIn(false);
    };

    return (
        <Router>
            <NetworkBanner />
            <OneSignalInit user={user} />
            <Suspense fallback={<LoadingScreen />}>
                <AnimatedRoutes
                    isLoggedIn={isLoggedIn}
                    user={user}
                    handleLogin={handleLogin}
                    handleLogout={handleLogout}
                />
            </Suspense>
        </Router>
    );
}

export default App;
